import { Autocomplete, Box, TextField } from '@mui/material';
import { RootContext } from 'components/RootContainer';
import { MenuItem } from 'components/styled/SelectMenuItem';
import { ReservationCardGroupModel } from 'pages/ReservationModule/pages/CreateReservationPageNew/models';
import { FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useDebounce } from 'use-debounce';
import { Chip } from '../../../../components/Chips';

export const CompanyField: FC<{
  value: ReservationCardGroupModel[] | null;
  onChange: (value: ReservationCardGroupModel[] | null) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation('reservation');

  const {
    HotelApi: {
      reservations: {
        reservation: { common: CommonService },
      },
    },
  } = useContext(RootContext);

  const [cardSearch, setCardSearch] = useState<string>('');

  const [searchDebounced] = useDebounce(cardSearch, 500);

  const { data: cards, isLoading: isCardsLoading } = useQuery({
    queryKey: ['search-company', searchDebounced],
    queryFn: () =>
      CommonService.searchCardGroup({
        search: searchDebounced,
        limit: 500,
        offset: 0,
      }),
    enabled: searchDebounced.length > 0,
  });

  const handleDelete = (index: number) => {
    const currentValue = value || [];
    const newValue = [...currentValue.slice(0, index), ...currentValue.slice(index + 1)];
    onChange(newValue.length > 0 ? newValue : null);
  };

  return (
    <Box
      sx={
        value?.length! > 0
          ? {
              boxShadow: 'inset 0 -1px 0 0 #d2d7de',
              pb: 2,
            }
          : {}
      }
    >
      <Autocomplete
        options={cards?.filter(card => !value?.some(v => v.id === card.id)) ?? []}
        loading={isCardsLoading}
        getOptionLabel={option => option.name ?? ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option, state) => (
          <MenuItem {...props} value={option.id} key={`${option?.id}-${option?.name}`}>
            {option.name}
          </MenuItem>
        )}
        value={null}
        onChange={(e, newValue) => {
          if (newValue) {
            const currentValue = value || [];
            onChange([...currentValue, newValue]);
          }
        }}
        inputValue={cardSearch}
        onInputChange={(e, value, reason) => {
          if (reason === 'clear') {
            setCardSearch('');
          } else {
            setCardSearch(value);
          }
        }}
        renderInput={props => (
          <TextField
            {...props}
            variant="standard"
            fullWidth
            label={t('generalInformations.company')}
            placeholder={t('generalInformations.companyPlaceholder')}
            inputProps={{
              ...props.inputProps,
              autoComplete: 'new-password',
            }}
          />
        )}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
        {value?.map((company, index) => (
          <Chip
            variant="filled"
            color="primary"
            key={`${company.id}-${index}`}
            label={company.name}
            onDelete={() => handleDelete(index)}
            size="small"
          />
        ))}
      </Box>
    </Box>
  );
};
